import { useMemo } from "react";

const MOOD_VALUES = { down: 1, flat: 2, ok: 3, good: 4, fire: 5 };
const MOOD_COLORS = {
  down: "#f43f5e",
  flat: "#94a3b8",
  ok: "#f59e0b",
  good: "#10b981",
  fire: "#f97316",
};

function last30Dates() {
  const out = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  for (let i = 29; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    out.push(`${y}-${m}-${day}`);
  }
  return out;
}

export default function MoodChart({ entries }) {
  const data = useMemo(() => {
    const dates = last30Dates();
    return dates.map((iso) => {
      const e = entries[iso];
      const v = e && MOOD_VALUES[e.mood] ? MOOD_VALUES[e.mood] : null;
      return { iso, mood: e?.mood || null, value: v };
    });
  }, [entries]);

  const total = data.filter((d) => d.value != null).length;

  const W = 600; // viewBox width (matches ~6:1 display aspect)
  const H = 100;
  const padX = 10;
  const padY = 10;
  const innerW = W - padX * 2;
  const innerH = H - padY * 2;

  const xFor = (i) => padX + (innerW * i) / (data.length - 1);
  const yFor = (v) => padY + innerH - ((v - 1) / 4) * innerH; // 1..5 -> bottom..top

  // Build a single polyline connecting all points that have values
  const points = data
    .map((d, i) => (d.value == null ? null : { x: xFor(i), y: yFor(d.value) }))
    .filter(Boolean);

  const linePath =
    points.length >= 2
      ? `M ${points[0].x} ${points[0].y} ` + points.slice(1).map((p) => `L ${p.x} ${p.y}`).join(" ")
      : null;

  const areaPath =
    points.length >= 2
      ? `M ${points[0].x} ${padY + innerH} L ${points[0].x} ${points[0].y} ` +
        points.slice(1).map((p) => `L ${p.x} ${p.y}`).join(" ") +
        ` L ${points[points.length - 1].x} ${padY + innerH} Z`
      : null;

  const avg =
    total > 0
      ? (
          data.filter((d) => d.value != null).reduce((s, d) => s + d.value, 0) / total
        ).toFixed(1)
      : null;

  const avgLabel = (() => {
    if (avg == null) return "—";
    const v = parseFloat(avg);
    if (v < 1.7) return "Dur · " + avg;
    if (v < 2.7) return "Neutre · " + avg;
    if (v < 3.7) return "OK · " + avg;
    if (v < 4.5) return "Bien · " + avg;
    return "Au top · " + avg;
  })();

  return (
    <div className="em-card p-6 lg:p-8" data-testid="mood-chart">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="text-white font-semibold text-lg font-[Outfit]">
            Mon humeur, 30 derniers jours
          </h3>
          <p className="text-sm text-slate-400 mt-1">
            {total === 0
              ? "Note ton humeur quelques jours pour voir ta courbe apparaître."
              : `${total} jour${total > 1 ? "s" : ""} enregistré${total > 1 ? "s" : ""} — moyenne ${avgLabel}`}
          </p>
        </div>
        <div className="flex items-center gap-3 text-xs text-slate-400">
          <span className="inline-flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full" style={{ background: MOOD_COLORS.down }} /> Dur
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full" style={{ background: MOOD_COLORS.ok }} /> OK
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full" style={{ background: MOOD_COLORS.fire }} /> Au top
          </span>
        </div>
      </div>

      <div className="mt-6 relative">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          preserveAspectRatio="none"
          className="w-full h-48"
          role="img"
          aria-label="Courbe d'humeur sur 30 jours"
        >
          {/* Horizontal grid lines */}
          {[1, 2, 3, 4, 5].map((lvl) => (
            <line
              key={lvl}
              x1={padX}
              x2={W - padX}
              y1={yFor(lvl)}
              y2={yFor(lvl)}
              stroke="rgba(255,255,255,0.06)"
              strokeWidth="0.5"
            />
          ))}

          {/* Area fill under line */}
          <defs>
            <linearGradient id="moodGrad" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="#10b981" stopOpacity="0.35" />
              <stop offset="100%" stopColor="#10b981" stopOpacity="0" />
            </linearGradient>
          </defs>
          {areaPath && <path d={areaPath} fill="url(#moodGrad)" />}

          {/* Line */}
          {linePath && (
            <path
              d={linePath}
              fill="none"
              stroke="#10b981"
              strokeWidth="2"
              strokeLinejoin="round"
              strokeLinecap="round"
              vectorEffect="non-scaling-stroke"
            />
          )}

          {/* Dots */}
          {data.map((d, i) =>
            d.value == null ? null : (
              <circle
                key={`p-${i}`}
                cx={xFor(i)}
                cy={yFor(d.value)}
                r="2"
                fill={MOOD_COLORS[d.mood] || "#10b981"}
                stroke="#0F172A"
                strokeWidth="1"
              >
                <title>{`${new Date(d.iso).toLocaleDateString("fr-FR")} — ${d.mood}`}</title>
              </circle>
            )
          )}
        </svg>

        {/* Date axis */}
        <div className="mt-2 flex justify-between text-[10px] text-slate-500 px-[2%]">
          <span>
            {new Date(data[0].iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" })}
          </span>
          <span>
            {new Date(data[Math.floor(data.length / 2)].iso).toLocaleDateString("fr-FR", {
              day: "2-digit",
              month: "short",
            })}
          </span>
          <span>
            {new Date(data[data.length - 1].iso).toLocaleDateString("fr-FR", {
              day: "2-digit",
              month: "short",
            })}
          </span>
        </div>
      </div>
    </div>
  );
}
